import {
  Badge,
  Button,
  Card,
  Group,
  Select,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import type { ReactElement } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { agentRoleErrorText } from "../agentRoles/refusalText";
import { useSupportedModels } from "../agentRoles/useSupportedModels";
import type { ConfigValue, Proposal, ProposalStatus } from "./api";
import { ChecklistTweakForm, isChecklistTweakReady } from "./checklistTweak";
import { ConfigUpdateForm, validateConfigValue } from "./configUpdate";
import { ToolBlacklistForm, isToolBlacklistReady } from "./toolBlacklist";
import { useApplyProposal } from "./useApplyProposal";
import { useConfigKeys } from "./useConfigKeys";
import { useRejectProposal } from "./useRejectProposal";

// Карточка одного предложения самообучения.
//
// Показывает вид (Badge), целевую роль, scope, текст предложения,
// уверенность модели (0..1 → проценты), число вмешательств-свидетельств
// и статус. Кнопки «Применить»/«Отклонить» — только для pending.
// «Применить» доступна для `prompt_tweak` (override промпта, без тела),
// `role_change` (override модели — человек подтверждает модель из списка
// активного провайдера в Select) и `config_update` (override org-config-
// ключа — человек выбирает ключ из реестра и вводит значение в его
// диапазоне). Остальные виды пока не реализованы (backend вернёт 422) —
// дизейблим заранее и поясняем Tooltip'ом. Ошибки apply/reject
// (409/422/404) показываем тостом с человекочитаемой причиной из `detail`.

// Уверенность приходит как доля 0..1 — переводим в проценты для подписи.
const PERCENT_FACTOR = 100;

// Виды, применяемые из UI. Остальные backend отвергает 422.
const PROMPT_TWEAK_KIND = "prompt_tweak";
const ROLE_CHANGE_KIND = "role_change";
const CONFIG_UPDATE_KIND = "config_update";
const CHECKLIST_TWEAK_KIND = "checklist_tweak";
const TOOL_BLACKLIST_KIND = "tool_blacklist";

function statusColor(status: ProposalStatus): string {
  switch (status) {
    case "applied":
      return "green";
    case "rejected":
      return "gray";
    case "obsolete":
      return "gray";
    default:
      return "blue";
  }
}

function confidencePercent(confidence: number): number {
  return Math.round(confidence * PERCENT_FACTOR);
}

// Аргументы apply-мутации, подтверждённые человеком в UI. Зависят от вида:
// role_change несёт { model }, config_update — { configKey, configValue },
// checklist_tweak — { repoFullName, checklistText }, prompt_tweak — { id }.
export interface ApplyArgs {
  readonly id: string;
  readonly model?: string;
  readonly configKey?: string;
  readonly configValue?: ConfigValue;
  readonly repoFullName?: string;
  readonly checklistText?: string;
  readonly roleName?: string;
  readonly toolName?: string;
}

// Поля формы checklist_tweak, подтверждённые человеком. Группируем в объект,
// чтобы не раздувать список позиционных аргументов buildApplyArgs.
export interface ChecklistInput {
  readonly repoFullName: string;
  readonly checklistText: string;
}

// Поля формы tool_blacklist, подтверждённые человеком. Роль приходит как
// `null` до выбора в Select. Группируем в объект — как ChecklistInput.
export interface ToolBlacklistInput {
  readonly roleName: string | null;
  readonly toolName: string;
}

// Чистая сборка аргументов apply из состояния формы. Вынесена отдельно,
// чтобы тестировать ветки напрямую: Mantine Select/NumberInput onChange в
// jsdom (floating-ui dropdown) не зовётся, поэтому ветку «выбрано» юнитим
// здесь. role_change с выбранной моделью → { id, model }; config_update с
// выбранным ключом и валидным значением → { id, configKey, configValue };
// checklist_tweak с непустыми repo+текстом → { id, repoFullName,
// checklistText }; tool_blacklist с выбранной ролью и непустым инструментом →
// { id, roleName, toolName }; иначе только { id } (prompt_tweak / форма не
// заполнена). Опускаем опциональные ключи вместо явного undefined —
// exactOptionalPropertyTypes.
export function buildApplyArgs(
  id: string,
  kind: string,
  selectedModel: string | null,
  configKey: string | null,
  configValue: ConfigValue | null,
  checklist: ChecklistInput,
  blacklist: ToolBlacklistInput,
): ApplyArgs {
  if (kind === ROLE_CHANGE_KIND && selectedModel !== null) {
    return { id, model: selectedModel };
  }
  if (
    kind === CONFIG_UPDATE_KIND &&
    configKey !== null &&
    configValue !== null
  ) {
    return { id, configKey, configValue };
  }
  if (
    kind === CHECKLIST_TWEAK_KIND &&
    isChecklistTweakReady(checklist.repoFullName, checklist.checklistText)
  ) {
    return {
      id,
      repoFullName: checklist.repoFullName.trim(),
      checklistText: checklist.checklistText.trim(),
    };
  }
  if (
    kind === TOOL_BLACKLIST_KIND &&
    blacklist.roleName !== null &&
    isToolBlacklistReady(blacklist.roleName, blacklist.toolName)
  ) {
    return {
      id,
      roleName: blacklist.roleName,
      toolName: blacklist.toolName.trim(),
    };
  }
  return { id };
}

// Состояние формы apply, влияющее на доступность кнопки «Применить».
export interface ApplyFormState {
  readonly kind: string;
  readonly busy: boolean;
  readonly selectedModel: string | null;
  readonly configKey: string | null;
  readonly configValue: ConfigValue | null;
  readonly checklist: ChecklistInput;
  readonly blacklist: ToolBlacklistInput;
}

// Чистое решение «кнопка Применить недоступна?». Вынесено отдельно: ветки
// зависят от состояния, которое нельзя выставить через Mantine
// Select/NumberInput в jsdom (onChange не зовётся), поэтому юнитим напрямую.
// role_change без модели, config_update без ключа/значения, checklist_tweak
// без repo/текста — disabled; неприменимый вид — тоже. prompt_tweak —
// всегда можно (если не busy).
export function isApplyDisabled(state: ApplyFormState): boolean {
  const canApply =
    state.kind === PROMPT_TWEAK_KIND ||
    state.kind === ROLE_CHANGE_KIND ||
    state.kind === CONFIG_UPDATE_KIND ||
    state.kind === CHECKLIST_TWEAK_KIND ||
    state.kind === TOOL_BLACKLIST_KIND;
  if (!canApply || state.busy) {
    return true;
  }
  if (state.kind === ROLE_CHANGE_KIND) {
    return state.selectedModel === null;
  }
  if (state.kind === CONFIG_UPDATE_KIND) {
    return state.configKey === null || state.configValue === null;
  }
  if (state.kind === CHECKLIST_TWEAK_KIND) {
    return !isChecklistTweakReady(
      state.checklist.repoFullName,
      state.checklist.checklistText,
    );
  }
  if (state.kind === TOOL_BLACKLIST_KIND) {
    return !isToolBlacklistReady(
      state.blacklist.roleName,
      state.blacklist.toolName,
    );
  }
  return false;
}

function RoleChangeSelect({
  selectedModel,
  setSelectedModel,
  busy,
  t,
}: {
  readonly selectedModel: string | null;
  readonly setSelectedModel: (value: string | null) => void;
  readonly busy: boolean;
  readonly t: TFunction;
}): ReactElement {
  const supported = useSupportedModels();
  const models = supported.data?.models ?? [];
  return (
    <Select
      label={t("selfImprovement.modelSelectLabel")}
      description={t("selfImprovement.modelSelectHint")}
      placeholder={t("selfImprovement.modelSelectPlaceholder")}
      data={[...models]}
      value={selectedModel}
      onChange={setSelectedModel}
      disabled={supported.isLoading || busy}
      error={
        supported.isError ? agentRoleErrorText(supported.error, t) : undefined
      }
    />
  );
}

function ProposalActions({
  proposal,
  t,
}: {
  readonly proposal: Proposal;
  readonly t: TFunction;
}): ReactElement {
  const applyMutation = useApplyProposal();
  const rejectMutation = useRejectProposal();
  // role_change: список моделей; config_update: реестр ключей. Оба
  // дедуплицируются react-query, поэтому несколько карточек = один fetch.
  const configKeys = useConfigKeys();
  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [configKey, setConfigKey] = useState<string | null>(null);
  const [rawValue, setRawValue] = useState<number | string | null>(null);
  const [repoFullName, setRepoFullName] = useState("");
  const [checklistText, setChecklistText] = useState("");
  const [blacklistRole, setBlacklistRole] = useState<string | null>(null);
  const [toolName, setToolName] = useState("");

  const isRoleChange = proposal.kind === ROLE_CHANGE_KIND;
  const isConfigUpdate = proposal.kind === CONFIG_UPDATE_KIND;
  const isChecklistTweak = proposal.kind === CHECKLIST_TWEAK_KIND;
  const isToolBlacklist = proposal.kind === TOOL_BLACKLIST_KIND;
  const canApply =
    proposal.kind === PROMPT_TWEAK_KIND ||
    isRoleChange ||
    isConfigUpdate ||
    isChecklistTweak ||
    isToolBlacklist;
  const busy = applyMutation.isPending || rejectMutation.isPending;

  // Валидное типизированное значение config_update (или null, если ключ
  // не выбран / значение вне диапазона) — нужно и для disabled, и для apply.
  const selectedSpec = (configKeys.data?.config_keys ?? []).find(
    (spec) => spec.key === configKey,
  );
  const configValue = validateConfigValue(selectedSpec, rawValue).value;
  const checklist = { repoFullName, checklistText };
  const blacklist = { roleName: blacklistRole, toolName };

  const applyDisabled = isApplyDisabled({
    kind: proposal.kind,
    busy,
    selectedModel,
    configKey,
    configValue,
    checklist,
    blacklist,
  });

  const handleApply = (): void => {
    const args = buildApplyArgs(
      proposal.id,
      proposal.kind,
      selectedModel,
      configKey,
      configValue,
      checklist,
      blacklist,
    );
    applyMutation.mutate(args, {
      onSuccess: () => {
        notifications.show({
          message: t("selfImprovement.applySuccess"),
          color: "green",
        });
      },
      onError: (error) => {
        notifications.show({
          message: t("selfImprovement.applyError", {
            message: error.message,
          }),
          color: "red",
        });
      },
    });
  };

  const handleReject = (): void => {
    rejectMutation.mutate(proposal.id, {
      onSuccess: () => {
        notifications.show({
          message: t("selfImprovement.rejectSuccess"),
          color: "green",
        });
      },
      onError: (error) => {
        notifications.show({
          message: t("selfImprovement.rejectError", { message: error.message }),
          color: "red",
        });
      },
    });
  };

  const applyButton = (
    <Button
      size="xs"
      onClick={handleApply}
      loading={applyMutation.isPending}
      disabled={applyDisabled}
    >
      {t("selfImprovement.applyButton")}
    </Button>
  );

  return (
    <Stack gap="xs">
      {isRoleChange ? (
        <RoleChangeSelect
          selectedModel={selectedModel}
          setSelectedModel={setSelectedModel}
          busy={busy}
          t={t}
        />
      ) : null}
      {isConfigUpdate ? (
        <ConfigUpdateForm
          configKey={configKey}
          setConfigKey={setConfigKey}
          rawValue={rawValue}
          setRawValue={setRawValue}
          busy={busy}
          t={t}
        />
      ) : null}
      {isChecklistTweak ? (
        <ChecklistTweakForm
          repoFullName={repoFullName}
          setRepoFullName={setRepoFullName}
          checklistText={checklistText}
          setChecklistText={setChecklistText}
          busy={busy}
          t={t}
        />
      ) : null}
      {isToolBlacklist ? (
        <ToolBlacklistForm
          roleName={blacklistRole}
          setRoleName={setBlacklistRole}
          toolName={toolName}
          setToolName={setToolName}
          busy={busy}
          t={t}
        />
      ) : null}
      <Group gap="xs">
        {canApply ? (
          applyButton
        ) : (
          <Tooltip label={t("selfImprovement.applyOnlyApplicable")}>
            <span>{applyButton}</span>
          </Tooltip>
        )}
        <Button
          size="xs"
          variant="default"
          onClick={handleReject}
          loading={rejectMutation.isPending}
          disabled={busy}
        >
          {t("selfImprovement.rejectButton")}
        </Button>
      </Group>
    </Stack>
  );
}

export function ProposalCard({
  proposal,
}: {
  readonly proposal: Proposal;
}): ReactElement {
  const { t } = useTranslation();
  const isPending = proposal.status === "pending";

  return (
    <Card withBorder padding="md" radius="md">
      <Stack gap="sm">
        <Group justify="space-between">
          <Group gap="xs">
            <Badge variant="light">
              {t(`selfImprovement.kind.${proposal.kind}`)}
            </Badge>
            <Badge color={statusColor(proposal.status)}>
              {t(`selfImprovement.status.${proposal.status}`)}
            </Badge>
          </Group>
          <Text size="sm" c="dimmed">
            {t("selfImprovement.confidence", {
              percent: confidencePercent(proposal.confidence),
            })}
          </Text>
        </Group>

        <Text>{proposal.proposal_text}</Text>

        <Group gap="lg">
          <Text size="sm" c="dimmed">
            {t("selfImprovement.target", { target: proposal.target })}
          </Text>
          <Text size="sm" c="dimmed">
            {t("selfImprovement.scope", { scope: proposal.scope })}
          </Text>
          <Text size="sm" c="dimmed">
            {t("selfImprovement.evidence", {
              count: proposal.evidence_event_ids.length,
            })}
          </Text>
        </Group>

        {proposal.applied_at !== null ? (
          <Text size="sm" c="dimmed">
            {t("selfImprovement.appliedAt", { at: proposal.applied_at })}
          </Text>
        ) : null}

        {isPending ? <ProposalActions proposal={proposal} t={t} /> : null}
      </Stack>
    </Card>
  );
}
